import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, WarningCircle, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSettingsRefresh } from "@/lib/settings";

export default function FxPanel() {
  const refreshSettings = useSettingsRefresh();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/fx/status");
      setStatus(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/fx/refresh");
      if (r.data.ok) {
        toast.success(`Refreshed ${r.data.updated_count} rates · ${r.data.alerts.length} alert(s)`);
        await load();
        refreshSettings?.();
      } else {
        toast.error(r.data.error || "Refresh failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  if (loading) return <div className="text-xs text-gos-muted">Loading FX status…</div>;
  if (!status) return null;

  return (
    <div data-testid="fx-panel" className="bg-gos-subtle/40 border border-gos-border rounded-sm p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <ArrowsClockwise size={12} weight="duotone" /> FX auto-refresh
          </div>
          <div className="text-xs text-gos-slate">
            Source: open.er-api.com (ECB-fed, free) · Interval: every 24h when enabled
          </div>
        </div>
        <button onClick={runNow} disabled={refreshing} data-testid="fx-refresh-now"
          className="btn-secondary text-xs !py-2">
          <ArrowsClockwise size={14} weight="bold" /> {refreshing ? "Refreshing…" : "Refresh now"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <KPI label="Auto-refresh" value={
          <span className={`text-xs font-medium ${status.enabled ? "text-emerald-700" : "text-gos-muted"}`}>
            {status.enabled ? "ON" : "OFF"}
          </span>
        } testid="fx-enabled-state" />
        <KPI label="Last refresh" value={
          status.last_refreshed_at
            ? <span className="text-xs text-gos-slate flex items-center gap-1"><Clock size={11} /> {fmtDate(status.last_refreshed_at)} · {status.last_refresh_trigger || "—"}</span>
            : <span className="text-xs text-gos-muted">never</span>
        } testid="fx-last-refresh" />
        <KPI label="Alert threshold" value={<span className="text-xs">{status.threshold_pct}%</span>} testid="fx-threshold" />
      </div>

      {status.alerts?.length > 0 && (
        <div className="bg-white border border-gos-border rounded-sm p-3 mb-2" data-testid="fx-alerts">
          <div className="overline mb-2 flex items-center gap-1"><WarningCircle size={12} weight="duotone" className="text-amber-600" /> Recent alerts (moved &gt; {status.threshold_pct}%)</div>
          <div className="space-y-1">
            {status.alerts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex justify-between text-xs">
                <span className="font-medium">{a.currency}</span>
                <span className="text-gos-slate">{a.old} → {a.new}</span>
                <span className="text-amber-700 font-medium">±{a.change_pct}%</span>
                <span className="text-gos-muted">{fmtDate(a.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {status.enabled && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 mt-2">
          <CheckCircle size={14} weight="duotone" /> Auto-refresh enabled — next run within 24h. You can also click <em>Refresh now</em> any time.
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-gos-border rounded-sm p-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gos-muted">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}
